import {cn} from '../../lib/utils'
import {iconMap, type IconName} from './__generated/icon-map'
import {iconVariants} from './icon-variants'
import type {VariantProps} from 'class-variance-authority'
import {type ComponentPropsWithoutRef, forwardRef} from 'react'

/**
 * ICON COMPONENT - PRECOMPILED ICON SYSTEM
 *
 * Icon components are precompiled from SVG at build time (see
 * `packages/ui/scripts/generate-icons.ts`), so they work in any bundler — Vite or
 * webpack/Next.js alike. No manual maintenance required for consumers.
 *
 * HOW TO ADD NEW ICONS:
 * ---------------------
 * 1. Add your SVG file to: packages/ui/src/assets/icons/
 * 2. Name it following the pattern: icon-{name}.svg (e.g., icon-calendar.svg)
 * 3. Run `pnpm nx run @susu/ui:generate-icons` (or let `nx-build` do it) to regenerate
 *    `src/components/Icon/__generated/` — that output is gitignored, not committed.
 *
 * NAMING CONVENTIONS:
 * ------------------
 * - File: icon-arrow-left.svg  →  Usage: <Icon name="arrow-left" />
 * - Generated component: ArrowLeftIcon (in `__generated/arrow-left-icon.tsx`)
 *
 * HOW TO USE:
 * -----------
 * Basic usage:
 *   <Icon name="search" />
 *
 * With size variants:
 *   <Icon name="search" size="small" />        // 16px
 *   <Icon name="search" size="large" />        // 24px
 *   <Icon name="search" size={32} />           // Custom size
 *
 * With color:
 *   <Icon name="search" color="black" />
 *   <Icon name="search" color="error" />
 *
 * With accessibility:
 *   <Icon name="search" ariaLabel="Search products" />  // For informative icons
 *   <Icon name="search" />                              // Decorative (auto aria-hidden)
 *
 * `<Icon name="..." />` is the only supported public API — generated components
 * in `__generated/` are internal to this lookup and not meant to be imported directly.
 */

// Get all available icons
const AVAILABLE_ICONS = Object.keys(iconMap)
export type {IconName}

type IconVariants = VariantProps<typeof iconVariants>

/**
 * Icon component props
 *
 * @property name - The icon identifier (e.g., 'search', 'arrow_left'). Must match a file in assets/icons/
 * @property size - Icon size: named variants ('small', 'medium', etc.) or numeric pixel value (16, 24, 32)
 * @property color - Color using design system tokens. Supports semantic states and basic colors
 * @property ariaLabel - Accessibility label for screen readers. Omit for decorative icons (auto adds aria-hidden)
 */
export interface IconProps extends Omit<ComponentPropsWithoutRef<'span'>, 'color'> {
  name: IconName
  size?: IconVariants['size'] | number
  color?: IconVariants['color']
  ariaLabel?: string
}

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=10269-5225&m=dev
 */
const Icon = forwardRef<HTMLSpanElement, IconProps>(
  ({name, className, size = 'medium', color = 'default', ariaLabel, ...props}, ref) => {
    const IconComponent = iconMap[name as IconName]
    const isNumericSize = typeof size === 'number'
    const inlineStyles = isNumericSize ? {height: `${size}px`} : undefined

    // Safety check: ensure IconComponent is actually a function (React component)
    const isValidComponent = IconComponent && typeof IconComponent === 'function'

    return (
      <span
        ref={ref}
        className={cn(
          isNumericSize ? iconVariants({color}) : iconVariants({size, color}),
          className,
        )}
        style={inlineStyles}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : 'true'}
        {...props}
      >
        {isValidComponent ? (
          <IconComponent className="h-full w-auto" />
        ) : (
          <span className="h-full w-auto bg-gray-200" title={`Icon: ${name}`} /> // placeholder
        )}
      </span>
    )
  },
)

Icon.displayName = 'Icon'

export {AVAILABLE_ICONS, Icon, iconVariants}
